import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import RefreshIcon from "@mui/icons-material/Refresh";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import moment from "moment";
import {
  CashflowViewMode,
  StatusFilter,
  PaymentStatusFilter,
  ChartStackingMode,
  STATUS_FILTER_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  VIEW_MODE_OPTIONS,
  CHART_STACKING_OPTIONS,
} from "../types";
import { Supplier } from "models/CashflowModel";

interface CashflowControlPanelProps {
  startMonth: string;
  endMonth: string;
  statusFilter: StatusFilter;
  paymentStatusFilter: PaymentStatusFilter;
  viewMode: CashflowViewMode;
  chartStackingMode: ChartStackingMode;
  selectedBrands: string[];
  selectedSupplierIds: number[];
  availableBrands: string[];
  suppliers: Supplier[];
  onStartMonthChange: (value: string) => void;
  onEndMonthChange: (value: string) => void;
  onStatusFilterChange: (value: StatusFilter) => void;
  onPaymentStatusFilterChange: (value: PaymentStatusFilter) => void;
  onViewModeChange: (value: CashflowViewMode) => void;
  onChartStackingModeChange: (value: ChartStackingMode) => void;
  onSelectedBrandsChange: (value: string[]) => void;
  onSelectedSupplierIdsChange: (value: number[]) => void;
  onManageSuppliers: () => void;
  onManagePOs: () => void;
  onRefresh: () => void;
}

export default function CashflowControlPanel({
  startMonth,
  endMonth,
  statusFilter,
  paymentStatusFilter,
  viewMode,
  chartStackingMode,
  selectedBrands,
  selectedSupplierIds,
  availableBrands,
  suppliers,
  onStartMonthChange,
  onEndMonthChange,
  onStatusFilterChange,
  onPaymentStatusFilterChange,
  onViewModeChange,
  onChartStackingModeChange,
  onSelectedBrandsChange,
  onSelectedSupplierIdsChange,
  onManageSuppliers,
  onManagePOs,
  onRefresh,
}: CashflowControlPanelProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
      {/* Row 1: Date range + Status tabs + Actions */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <DatePicker
          label="Start Month"
          views={["year", "month"]}
          value={moment(startMonth, "YYYY-MM")}
          onChange={(newValue: any) => {
            if (newValue && newValue.isValid()) {
              onStartMonthChange(newValue.format("YYYY-MM"));
            }
          }}
          slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
        />
        <DatePicker
          label="End Month"
          views={["year", "month"]}
          value={moment(endMonth, "YYYY-MM")}
          onChange={(newValue: any) => {
            if (newValue && newValue.isValid()) {
              onEndMonthChange(newValue.format("YYYY-MM"));
            }
          }}
          slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
        />

        <Tabs
          value={statusFilter}
          onChange={(_, newValue) => onStatusFilterChange(newValue)}
          sx={{ minHeight: 36 }}
        >
          {STATUS_FILTER_OPTIONS.map(opt => (
            <Tab key={opt.value} label={opt.label} value={opt.value} sx={{ minHeight: 36, py: 0 }} />
          ))}
        </Tabs>

        <Tabs
          value={paymentStatusFilter}
          onChange={(_, newValue) => onPaymentStatusFilterChange(newValue)}
          sx={{ minHeight: 36 }}
        >
          {PAYMENT_STATUS_OPTIONS.map(opt => (
            <Tab key={opt.value} label={opt.label} value={opt.value} sx={{ minHeight: 36, py: 0 }} />
          ))}
        </Tabs>

        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={onManageSuppliers}
          >
            Suppliers
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<ListAltIcon />}
            onClick={onManagePOs}
          >
            Manage POs
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Row 2: Filters + View toggles */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Brand</InputLabel>
          <Select
            multiple
            value={selectedBrands}
            label="Brand"
            onChange={(e) => onSelectedBrandsChange(e.target.value as string[])}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map(v => <Chip key={v} label={v} size="small" />)}
              </Box>
            )}
          >
            {availableBrands.map(b => (
              <MenuItem key={b} value={b}>{b}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Supplier</InputLabel>
          <Select
            multiple
            value={selectedSupplierIds}
            label="Supplier"
            onChange={(e) => onSelectedSupplierIdsChange(e.target.value as number[])}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as number[]).map(id => {
                  const s = suppliers.find(s => s.id === id);
                  return <Chip key={id} label={s?.name || id} size="small" />;
                })}
              </Box>
            )}
          >
            {suppliers.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, val) => { if (val) onViewModeChange(val); }}
          >
            {VIEW_MODE_OPTIONS.map(opt => (
              <ToggleButton key={opt.value} value={opt.value} sx={{ textTransform: 'none' }}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={chartStackingMode}
            onChange={(_, val) => { if (val) onChartStackingModeChange(val); }}
          >
            {CHART_STACKING_OPTIONS.map(opt => (
              <ToggleButton key={opt.value} value={opt.value} sx={{ textTransform: 'none' }}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
    </Box>
  );
}
